import { useAuthStore } from '@/stores/auth';
import type { TokenPair } from 'shared';

const API_URL = import.meta.env.VITE_API_URL || '';

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

/**
 * Error thrown for any non-2xx response. Carries the HTTP status and the
 * decoded body so callers can branch on `error.status === 409` (etc.) and
 * read structured fields like `error.body.activeVisit`.
 */
export class ApiClientError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.body = body;
  }
}

/** Result type for endpoints that may return 204 No Content. */
export type MaybeEmpty<T> = T | null;

class ApiClient {
  private refreshPromise: Promise<TokenPair> | null = null;

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { skipAuth = false, ...fetchOptions } = options;
    const { accessToken, refreshToken, setTokens, logout } = useAuthStore.getState();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    };

    if (!skipAuth && accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
    }

    let response = await fetch(`${API_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
    });

    // Handle token refresh on 401
    if (response.status === 401 && refreshToken && !skipAuth) {
      try {
        const tokens = await this.refreshTokens(refreshToken);
        setTokens(tokens);

        // Retry with new token
        (headers as Record<string, string>)['Authorization'] = `Bearer ${tokens.accessToken}`;
        response = await fetch(`${API_URL}${endpoint}`, {
          ...fetchOptions,
          headers,
        });
      } catch {
        logout();
        throw new ApiClientError('A sua sessão expirou. Inicie sessão novamente.', 401, null);
      }
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({ message: 'Ocorreu um erro. Tente novamente.' }));
      const message =
        (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string'
          ? body.message
          : null) ?? `HTTP ${response.status}`;
      throw new ApiClientError(message, response.status, body);
    }

    // Handle 204 No Content / empty body — surface as null so callers can
    // distinguish "no resource" from a real object.
    if (response.status === 204) {
      return null as T;
    }
    const text = await response.text();
    return text ? JSON.parse(text) : (null as T);
  }

  private async refreshTokens(refreshToken: string): Promise<TokenPair> {
    // Deduplicate concurrent refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Route refresh through the kind-specific endpoint so participant tokens
    // hit /api/auth/participant/refresh. Legacy tokens (no kind set) default
    // to the staff alias at /api/auth/refresh for backward compatibility.
    const { kind } = useAuthStore.getState();
    const endpoint = kind ? `/api/auth/${kind}/refresh` : '/api/auth/refresh';

    this.refreshPromise = this.request<TokenPair>(endpoint, {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
      skipAuth: true,
    }).finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  // Convenience methods
  get<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  put<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export const api = new ApiClient();
